/**
 * AdjacencyMapApp — main adjacency map component tree.
 *
 * Adapted from standalone App.tsx. Removed MSAL provider, SharePoint
 * sync UI, IWT logo header. Keeps all 7 views and the core layout.
 */

import React, { useEffect } from 'react';
import { useAdjacencyStore } from './store/adjacency-store';
import { ResourceSettings } from './components/shared/ResourceSettings';
import { ViewSwitcher } from './components/nav/ViewSwitcher';
import { MapLegend } from './components/map/MapLegend';
import { FilterBar } from './components/shared/FilterBar';
import { AdjacencyMap } from './components/map/AdjacencyMap';
import { ProductTable } from './components/list/ProductTable';
import { PriorityMatrix } from './components/matrix/PriorityMatrix';
import { PortfolioDashboard } from './components/dashboard/PortfolioDashboard';
import { FinanceDashboard } from './components/finance/FinanceDashboard';
import { ResourceDashboard } from './components/resources/ResourceDashboard';
import { TimelineView } from './components/timeline/TimelineView';
import { useUndoShortcuts } from './hooks/useUndoShortcuts';
import './styles/glass.css';

interface AdjacencyMapAppProps {
  /** Called when the component is ready (data loaded). */
  onReady?: () => void;
  /** Optional extras rendered in the toolbar next to the node count (e.g. WQ-210 sync chip). */
  headerExtras?: React.ReactNode;
}

export function AdjacencyMapApp({ onReady, headerExtras }: AdjacencyMapAppProps) {
  const { isLoading, viewMode, loadNodes, nodes, error, clearError, undo, redo } = useAdjacencyStore();

  useEffect(() => {
    void loadNodes().then(() => onReady?.());
  }, [loadNodes, onReady]);

  // WQ-340 (D2): app-wide undo/redo — every view, not just the Map.
  useUndoShortcuts(undo, redo);

  const isMapView = viewMode === 'map';

  return (
    <div className="adjacency-map-scope flex flex-col" style={{ height: 'calc(100vh - 60px)', minHeight: '400px' }}>
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2" style={{ borderBottom: '1px solid var(--border, rgba(255,255,255,0.1))' }}>
        <div className="flex items-center gap-3 text-sm" style={{ color: 'var(--text-secondary, #999)' }}>
          <span className="px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ background: 'var(--filter-pill-bg)' }}>
            {nodes.length} nodes
          </span>
          {headerExtras}
          {error && (
            <span
              role="alert"
              className="flex items-center gap-2 px-2.5 py-0.5 rounded-full text-xs font-medium"
              style={{ background: 'rgba(239,68,68,0.15)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.35)' }}
            >
              {error}
              <button type="button" aria-label="Dismiss" onClick={clearError} style={{ color: 'inherit' }}>
                ×
              </button>
            </span>
          )}
        </div>
        <ResourceSettings />
      </div>

      {/* View Tabs */}
      <ViewSwitcher />

      {/* Main Content */}
      <main className="flex-1 flex flex-col p-3 min-h-0 min-w-0 relative">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <p style={{ color: 'var(--text-muted, #666)' }}>Loading adjacency map...</p>
          </div>
        ) : (
          <>
            {!isMapView && <FilterBar />}

            {isMapView ? (
              <AdjacencyMap />
            ) : viewMode === 'list' ? (
              <ProductTable />
            ) : viewMode === 'matrix' ? (
              <PriorityMatrix />
            ) : viewMode === 'finance' ? (
              <FinanceDashboard />
            ) : viewMode === 'resources' ? (
              <ResourceDashboard />
            ) : viewMode === 'timeline' ? (
              <TimelineView />
            ) : (
              <PortfolioDashboard />
            )}

            {isMapView && (
              <div className="absolute bottom-3 left-3 z-20">
                <MapLegend />
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
